import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { DataContext } from '../../../context/DataContext';
import { getRol, getToken, getUser } from '../../../api/Account';
import { getProyeccionInteresesAsync } from '../../../api/Report';
import { getGestoresBySucursalAccessAsync, getSucursalByUserAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { importe, fechaCorta } from '../../../helpers/formato';
import ReporteAgrupado from '../../../components/reportes/ReporteAgrupado';

const TODOS = 't';

const COLUMNAS = [
	{ key: 'id', label: '#', align: 'center', valor: (c) => c.id, excel: (c) => c.id },
	{ key: 'fullName', label: 'Cliente', align: 'left', valor: (c) => c.fullName },
	{ key: 'phone', label: 'Telefono', align: 'center', valor: (c) => c.phone },
	{
		key: 'diaAtencion',
		label: 'D.Atencion',
		align: 'center',
		valor: (c) => c.diaAtencion ?? ''
	},
	{
		key: 'cuotasPendientes',
		label: 'Cuotas',
		align: 'center',
		valor: (c) => c.cuotasPendientes,
		excel: (c) => c.cuotasPendientes
	},
	{
		key: 'proximoVencimiento',
		label: 'Proximo Vence',
		align: 'center',
		valor: (c) => fechaCorta(c.proximoVencimiento)
	},
	{
		key: 'interesVencido',
		label: 'Interes Vencido',
		align: 'right',
		total: true,
		formatoTotal: importe,
		valor: (c) => importe(c.interesVencido),
		excel: (c) => c.interesVencido
	},
	{
		key: 'interesPorVencer',
		label: 'Interes por Vencer',
		align: 'right',
		total: true,
		formatoTotal: importe,
		valor: (c) => importe(c.interesPorVencer),
		excel: (c) => c.interesPorVencer
	},
	{
		key: 'interesProyectado',
		label: 'Total Proyectado',
		align: 'right',
		total: true,
		formatoTotal: importe,
		valor: (c) => importe(c.interesProyectado),
		excel: (c) => c.interesProyectado
	}
];

const ProyeccionIntereses = () => {
	const [data, setData] = useState([]);
	const [gestorList, setGestorList] = useState([]);
	const [sucursalName, setSucursalName] = useState('Todas');
	const [gestorName, setGestorName] = useState('Todos');

	const { selectedSucursal, selectedGestor, fechaHasta } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);

			const [reporte, sucursales, gestores] = await Promise.all([
				getProyeccionInteresesAsync(token, {
					sucursalId: selectedSucursal === TODOS ? 0 : Number(selectedSucursal),
					gestorId: selectedGestor === TODOS ? 0 : Number(selectedGestor),
					fechaHasta
				}),
				getSucursalByUserAsync(token),
				getGestoresBySucursalAccessAsync(token)
			]);

			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (gestores.statusResponse) {
				setGestorList(gestores.data.result);
				if (selectedGestor !== TODOS) {
					const ges = gestores.data.result.find(
						(g) => g.id.toString() === selectedGestor
					);
					setGestorName(ges ? ges.fullName : selectedGestor);
				}
			}
			if (selectedSucursal !== TODOS && sucursales.statusResponse) {
				const suc = sucursales.data.result.find(
					(s) => s.id.toString() === selectedSucursal
				);
				setSucursalName(suc ? suc.description : selectedSucursal);
			}
		})();
	}, [token, selectedSucursal, selectedGestor, fechaHasta, setIsLoading, manejarError]);

	// El servicio devuelve el id del gestor; el nombre sale de la lista ya cargada.
	const nombreGestor = useCallback(
		(fila) => {
			const gestor = gestorList.find((g) => g.id === fila.gestor);
			return gestor ? gestor.fullName : 'Sin gestor';
		},
		[gestorList]
	);

	return (
		<ReporteAgrupado
			tituloReporte="Proyeccion de Recuperacion de Intereses"
			nombreArchivo="ProyeccionIntereses"
			parametros={[
				{ label: 'Sucursal', valor: sucursalName },
				{ label: 'Gestor', valor: gestorName },
				{ label: 'Proyectado hasta', valor: moment(fechaHasta).format('L') }
			]}
			columnas={COLUMNAS}
			data={data}
			agruparPor={nombreGestor}
			etiquetaGrupo="Gestor"
			contexto={{ title, slogan, icon, user, rol }}
		/>
	);
};

export default ProyeccionIntereses;
